import { ref, shallowRef } from 'vue';
import { decodeRoundDataFromUrl } from '../utils/demoRoundData';
import { isCancellation, pollRoundUrl, reportRoundMissing } from '../utils/round_url_poller';

/**
 * Round loading for the Restrat tab (match pages, demo page).
 *
 * roundState: 'idle' | 'loading' | 'preparing' | 'processing' | 'gone' | 'failed'
 * - preparing: the round is being built (202), usually seconds
 * - processing: the demo is being re-parsed, minutes; retry once it lands
 * - gone: 410, nothing left to rebuild the round from
 * - failed: gave up polling, or the artifact did not decode
 */
// eslint-disable-next-line import-x/prefer-default-export
export function useRestratRound({ demoId, teamId, isDemoReady }) {
  const roundData = shallowRef(null);
  const roundState = ref('idle');
  let generation = 0;
  let controller = null;

  function cancel() {
    generation += 1;
    controller?.abort();
    controller = null;
  }

  function settle(state) {
    roundData.value = null;
    roundState.value = state;
  }

  async function loadRound(round) {
    cancel();
    const myGeneration = generation;
    const isCurrent = () => myGeneration === generation;
    const abortController = new AbortController();
    controller = abortController;
    const { signal } = abortController;

    if (!isDemoReady()) {
      settle('idle');
      return;
    }

    const id = demoId();
    const team = teamId();
    roundState.value = 'loading';

    const result = await pollRoundUrl({
      demoId: id,
      round,
      teamId: team,
      signal,
      isCurrent,
      onPreparing: () => { roundState.value = 'preparing'; },
    });
    if (!isCurrent()) return;

    if (result.kind === 'processing') { settle('processing'); return; }
    if (result.kind === 'gone') { settle('gone'); return; }
    if (result.kind !== 'ready') { settle('failed'); return; }

    try {
      const bufferUrl = import.meta.env.VITE_ENVIRONMENT !== 'local.' ? result.url : '/round-7.buf';
      const decoded = await decodeRoundDataFromUrl(bufferUrl, signal);
      if (!isCurrent()) return;
      roundData.value = decoded;
      roundState.value = 'idle';
    } catch (error) {
      if (isCancellation(error) || signal.aborted) return;
      console.error('Error loading round:', error);
      // Server truth about this round's artifact, so reported even if the viewer moved on.
      await reportRoundMissing({ demoId: id, round, teamId: team });
      if (isCurrent()) settle('failed');
    }
  }

  return {
    roundData, roundState, loadRound, cancel,
  };
}
